import { useState, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { generateClaim } from '../services/api';
import RevenueCodeCombobox from '../components/RevenueCodeCombobox';
import CptCodeCombobox from '../components/CptCodeCombobox';

const ADMISSION_TYPES = [
  { value: '', label: 'Select...' },
  { value: '1', label: '1 – Emergency' },
  { value: '2', label: '2 – Urgent' },
  { value: '3', label: '3 – Elective' },
  { value: '4', label: '4 – Newborn' },
  { value: '5', label: '5 – Trauma' },
  { value: '9', label: '9 – Info Not Available' },
];

const ADMISSION_SOURCES = [
  { value: '', label: 'Select...' },
  { value: '1', label: '1 – Non-health Care Facility' },
  { value: '2', label: '2 – Clinic / Physician Referral' },
  { value: '4', label: '4 – Transfer from Hospital' },
  { value: '5', label: '5 – Transfer from SNF' },
  { value: '6', label: '6 – Transfer from Another Facility' },
  { value: '7', label: '7 – Emergency Room' },
  { value: '8', label: '8 – Court/Law Enforcement' },
  { value: '9', label: '9 – Information Not Available' },
];

const DISCHARGE_STATUSES = [
  { value: '', label: 'Select...' },
  { value: '01', label: '01 – Discharged to Home' },
  { value: '02', label: '02 – Short-term Hospital' },
  { value: '03', label: '03 – Skilled Nursing Facility' },
  { value: '04', label: '04 – Intermediate Care' },
  { value: '05', label: '05 – Cancer Center' },
  { value: '06', label: '06 – Home Under Care' },
  { value: '07', label: '07 – AMA (Left Against Advice)' },
  { value: '20', label: '20 – Expired' },
  { value: '30', label: '30 – Still Patient' },
  { value: '43', label: '43 – Federal Hospital' },
  { value: '62', label: '62 – Inpatient Rehab' },
  { value: '65', label: '65 – Psychiatric Hospital' },
];

// Present On Admission (POA) — CMS reporting options for 837I.
// Blank is permitted (e.g. for diagnoses on the POA-exempt list).
const POA_OPTIONS = [
  { value: '',  label: '—' },
  { value: 'Y', label: 'Y – Yes' },
  { value: 'N', label: 'N – No' },
  { value: 'U', label: 'U – Unknown' },
  { value: 'W', label: 'W – Undetermined' },
  { value: '1', label: '1 – Exempt' },
];

// 837I Loop 2310 entity identifiers available for FL 78/79 "Other" provider slots.
const OTHER_PROVIDER_QUALIFIERS = [
  { value: 'DN', label: 'DN – Referring' },
  { value: '82', label: '82 – Rendering' },
  { value: 'ZZ', label: 'ZZ – Other Operating' },
];

// Parse a free-form physician name into {lastName, firstName, middleName}.
// Handles "Smith, John M", "Dr. John M Smith", or single-token names.
function parsePhysicianName(name) {
  if (!name) return { firstName: '', middleName: '', lastName: '' };
  const trimmed = String(name).replace(/^\s*(dr\.?|mr\.?|mrs\.?|ms\.?)\s+/i, '').trim();
  if (!trimmed) return { firstName: '', middleName: '', lastName: '' };
  if (trimmed.includes(',')) {
    const [last, rest = ''] = trimmed.split(',').map((s) => s.trim());
    const parts = rest.split(/\s+/).filter(Boolean);
    return {
      lastName:   last,
      firstName:  parts[0] || '',
      middleName: parts.slice(1).join(' '),
    };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { lastName: parts[0], firstName: '', middleName: '' };
  return {
    lastName:   parts[parts.length - 1],
    firstName:  parts[0],
    middleName: parts.slice(1, -1).join(' '),
  };
}

function createEmptyRevenueLine() {
  return {
    revenueCode: '',
    description: '',
    hcpcs: '',
    modifiers: '',
    serviceDate: '',
    units: '',
    totalCharges: '',
    nonCoveredCharges: '',
  };
}

const REL_TO_1450 = {
  Self: '18', Spouse: '01', Child: '19',
  Employee: '20', 'Life Partner': '53', Other: 'G8',
};

function Section({ icon, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="gc-section">
      <div className="gc-section-header" onClick={() => setOpen(!open)}>
        <div className="gc-section-header-left">
          <div className="gc-section-icon">{icon}</div>
          <h3 className="gc-section-title">{title}</h3>
        </div>
        <div className={`gc-section-toggle ${open ? 'open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
      {open && <div className="gc-section-body">{children}</div>}
    </div>
  );
}

function CMS1450Form({ claim, facility, onCancel, isEdit, returnTo, cobMode, cobOrder, cobPayer, cobPriorRemit }) {
  const navigate = useNavigate();
  const [activePayer, setActivePayer] = useState(cobMode && cobOrder > 1 ? cobOrder - 1 : 0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // The form card clips overflow, so the revenue-code dropdown would be hidden. Count how
  // many revenue menus are open and reserve room beneath the table only while one is showing.
  const [openRevenueMenus, setOpenRevenueMenus] = useState(0);
  const handleRevenueMenuOpenChange = (isOpen) =>
    setOpenRevenueMenus((n) => Math.max(0, n + (isOpen ? 1 : -1)));

  const pendingDateRef = useRef('');
  const onDateKeyDown = (value) => (e) => {
    if (e.key !== 'Tab' || !value) return;
    e.preventDefault();
    pendingDateRef.current = value;
    const form = e.currentTarget.closest('form');
    if (!form) return;
    const focusable = Array.from(form.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    ));
    const idx = focusable.indexOf(e.currentTarget);
    const next = e.shiftKey ? focusable[idx - 1] : focusable[idx + 1];
    if (next) next.focus();
  };
  const onDateFocus = (_current, setter) => () => { if (pendingDateRef.current) setter(pendingDateRef.current); pendingDateRef.current = ''; };

  const [formData, setFormData] = useState(() => {
    const fmtDate = (val) => (val ? String(val).slice(0, 10) : '');

    // Revenue / service lines — prefer detailed service_lines from fetchWorklistItem,
    // fall back to the cpt_codes summary from the worklist row.
    //
    // COB: a secondary/tertiary claim still bills the FULL ORIGINAL charges, never the
    // balance left after the primary paid. The prior payer's payment and adjustments are
    // reported separately in the 837 COB loops (AMT*D + CAS), and the clearinghouse balances
    // charge = prior paid + adjustments. Reducing the line to the forwarded balance breaks
    // that edit ("Adjustment Amounts total X but must total the amount Not Paid"). The
    // balance is still conveyed to the payer via FL55 Estimated Amount Due (see the
    // cobPayerData estimatedAmountDue in handleSubmit).
    const initialServices = (() => {
      if (claim?.service_lines?.length) {
        return claim.service_lines.map((sl) => ({
          revenueCode:       sl.revenue_code        || '',
          description:       sl.service_description || '',
          hcpcs:             sl.cpt_hcpcs_code       || '',
          modifiers:         sl.modifiers            || '',
          serviceDate:       fmtDate(sl.service_date_from),
          units:             sl.units != null         ? String(sl.units)   : '',
          totalCharges:      sl.charges != null ? String(sl.charges) : '',
          nonCoveredCharges: sl.non_covered_charges != null ? String(sl.non_covered_charges) : '',
        }));
      }
      const cptCodes = claim?.cpt_codes || [];
      return cptCodes.length
        ? cptCodes.map((code) => ({
            revenueCode: '', description: '', hcpcs: code, modifiers: '',
            serviceDate: fmtDate(claim?.service_date),
            units: '1',
            totalCharges: '',
            nonCoveredCharges: '',
          }))
        : [createEmptyRevenueLine()];
    })();

    // Diagnoses — reconstruct structured object from DB rows keyed by diagnosis_type
    const buildDiagnoses = () => {
      const rows   = claim?.diagnoses || [];
      const result = {
        icdVersion:            claim?.icd_version || '0',
        principal:             '',
        principalPoa:          '',
        other:                 Array(17).fill(''),
        otherPoa:              Array(17).fill(''),
        admitting:             '',
        admittingPoa:          '',
        reasonForVisit:        Array(3).fill(''),
        ppsCode:               claim?.pps_code || '',
        externalCause:         Array(3).fill(''),
        principalProcedure:    '',
        principalProcedureDate:'',
        otherProcedures:       Array(5).fill(''),
        otherProcedureDates:   Array(5).fill(''),
      };
      let otherIdx = 0, rfvIdx = 0, ecIdx = 0;
      for (const dx of rows) {
        switch (dx.diagnosis_type) {
          case 'principal':
            result.principal    = dx.icd_code      || '';
            result.principalPoa = dx.poa_indicator || '';
            break;
          case 'admitting':
            result.admitting    = dx.icd_code      || '';
            result.admittingPoa = dx.poa_indicator || '';
            break;
          case 'reason_for_visit':
            if (rfvIdx < 3) result.reasonForVisit[rfvIdx++] = dx.icd_code || '';
            break;
          case 'external_cause':
            if (ecIdx < 3) result.externalCause[ecIdx++] = dx.icd_code || '';
            break;
          default:
            if (otherIdx < 17) {
              result.other[otherIdx]    = dx.icd_code      || '';
              result.otherPoa[otherIdx] = dx.poa_indicator || '';
              otherIdx++;
            }
        }
      }
      // ICD-10-PCS procedures
      const procs     = claim?.procedures || [];
      const principal = procs.find((p) => p.is_principal);
      const others    = procs.filter((p) => !p.is_principal);
      if (principal) {
        result.principalProcedure     = principal.procedure_code || '';
        result.principalProcedureDate = fmtDate(principal.procedure_date);
      }
      others.forEach((proc, i) => {
        if (i < 5) {
          result.otherProcedures[i]    = proc.procedure_code || '';
          result.otherProcedureDates[i]= fmtDate(proc.procedure_date);
        }
      });
      return result;
    };

    // Condition codes
    const conditionCodesArr = Array(11).fill('');
    (claim?.condition_codes || []).forEach((cc, i) => {
      if (i < 11) conditionCodesArr[i] = cc.condition_code || '';
    });

    // Occurrence codes & occurrence span codes
    const occurrenceCodesArr  = Array(8).fill('');
    const occurrenceDatesArr  = Array(8).fill('');
    const spanCodesArr        = Array(4).fill('');
    const spanFromArr         = Array(4).fill('');
    const spanThroughArr      = Array(4).fill('');
    let occIdx = 0, spanIdx = 0;
    (claim?.occurrence_codes || []).forEach((occ) => {
      if (occ.code_type === 'span') {
        if (spanIdx < 4) {
          spanCodesArr[spanIdx]   = occ.occurrence_code || '';
          spanFromArr[spanIdx]    = fmtDate(occ.from_date);
          spanThroughArr[spanIdx] = fmtDate(occ.through_date);
          spanIdx++;
        }
      } else {
        if (occIdx < 8) {
          occurrenceCodesArr[occIdx] = occ.occurrence_code || '';
          occurrenceDatesArr[occIdx] = fmtDate(occ.from_date);
          occIdx++;
        }
      }
    });

    // Value codes
    const valueCodesArr   = Array(12).fill('');
    const valueAmountsArr = Array(12).fill('');
    (claim?.value_codes || []).forEach((vc, i) => {
      if (i < 12) {
        valueCodesArr[i]   = vc.value_code || '';
        valueAmountsArr[i] = vc.amount != null ? String(vc.amount) : '';
      }
    });

    // Physicians — use saved provider fields when available.
    // For new claims, attending falls back to rendering_provider (set during EMR sync).
    // other1 (DN qualifier = referring) falls back to referring_provider when not yet set.
    const attendingName = claim?.attending_provider
      ? parsePhysicianName(claim.attending_provider)
      : parsePhysicianName(claim?.rendering_provider || claim?.provider);
    const operatingName = claim?.operating_provider
      ? parsePhysicianName(claim.operating_provider)
      : { lastName: '', firstName: '', middleName: '' };
    const other1Name = claim?.other_provider_1
      ? parsePhysicianName(claim.other_provider_1)
      : claim?.referring_provider
        ? parsePhysicianName(claim.referring_provider)
        : { lastName: '', firstName: '', middleName: '' };
    const other2Name = claim?.other_provider_2
      ? parsePhysicianName(claim.other_provider_2)
      : { lastName: '', firstName: '', middleName: '' };

    return {
      provider: {
        name:             facility?.name         || '',
        address:          facility?.address      || '',
        city:             facility?.city         || '',
        state:            facility?.state        || '',
        zip:              facility?.zip          || '',
        phone:            facility?.phone        || '',
        payToName:        '',
        payToAddress:     '',
        federalTaxNumber: facility?.taxId        || '',
        npi:              facility?.npi          || '',
        typeOfBill:       claim?.type_of_bill    || '',
        taxonomyCode:     facility?.taxonomyCode || '',
      },
      patient: {
        controlNumber:       claim?.encounter_id          || '',
        // Falls back to the patient's MRN on file at this facility so a first-time
        // claim opens pre-filled instead of blank.
        medicalRecordNumber: claim?.medical_record_number || claim?.patient_facility_mrn || '',
        name:                claim?.patient_name          || '',
        address:             claim?.patient?.street_address || '',
        city:                claim?.patient?.city           || '',
        state:               claim?.patient?.state          || '',
        zip:                 claim?.patient?.zip            || '',
        birthDate:           fmtDate(claim?.patient?.date_of_birth),
        sex:                 claim?.patient?.sex            || '',
        admissionDate:       fmtDate(claim?.admission_date),
        admissionHour:       claim?.admission_hour
                               ? String(claim.admission_hour).padStart(2, '0') + ':00'
                               : '',
        admissionType:       claim?.admission_type   || '',
        admissionSource:     claim?.admission_source || '',
        dischargeHour:       claim?.discharge_hour
                               ? String(claim.discharge_hour).padStart(2, '0') + ':00'
                               : '',
        dischargeStatus:     claim?.discharge_status || '',
      },
      statementFrom:         claim?.statement_from
                               ? fmtDate(claim.statement_from)
                               : fmtDate(claim?.service_date),
      statementThrough:      claim?.statement_through
                               ? fmtDate(claim.statement_through)
                               : fmtDate(claim?.service_date),
      conditionCodes:        conditionCodesArr,
      occurrenceCodes:       occurrenceCodesArr,
      occurrenceDates:       occurrenceDatesArr,
      occurrenceSpanCodes:   spanCodesArr,
      occurrenceSpanFrom:    spanFromArr,
      occurrenceSpanThrough: spanThroughArr,
      valueCodes:            valueCodesArr,
      valueAmounts:          valueAmountsArr,
      services:              initialServices,
      diagnoses: buildDiagnoses(),
      physicians: {
        attending: {
          lastName:     attendingName.lastName,
          firstName:    attendingName.firstName,
          middleName:   attendingName.middleName,
          npi:          claim?.attending_provider_npi || claim?.rendering_provider_npi || '',
          taxonomyCode: claim?.attending_provider_taxonomy || '',
        },
        operating: {
          lastName:  operatingName.lastName,
          firstName: operatingName.firstName,
          middleName:operatingName.middleName,
          npi:       claim?.operating_provider_npi || '',
        },
        other1: {
          qualifier:  claim?.other_provider_1_qualifier || 'DN',
          lastName:   other1Name.lastName,
          firstName:  other1Name.firstName,
          middleName: other1Name.middleName,
          npi:        claim?.other_provider_1_npi || (claim?.other_provider_1 ? '' : claim?.referring_provider_npi || ''),
        },
        other2: {
          qualifier:  claim?.other_provider_2_qualifier || '82',
          lastName:   other2Name.lastName,
          firstName:  other2Name.firstName,
          middleName: other2Name.middleName,
          npi:        claim?.other_provider_2_npi || '',
        },
      },
      payerFinancials: [0, 1, 2].map((i) => ({
        priorPayments:      claim?.payers?.[i]?.prior_payments      != null ? String(claim.payers[i].prior_payments)      : '',
        estimatedAmountDue: claim?.payers?.[i]?.estimated_amount_due != null ? String(claim.payers[i].estimated_amount_due) : '',
      })),
      remarks: claim?.notes || '',
    };
  });

  const updateField = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateTopLevel = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData((prev) => {
      const arr = [...prev[field]];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const updateService = (index, field, value) => {
    setFormData((prev) => {
      const newServices = prev.services.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      );
      return { ...prev, services: newServices };
    });
  };

  // A revenue code was resolved (picked from the dropdown, or an exact code typed and
  // confirmed on blur). The revenue-code dropdown only fills the code itself — the line
  // description now comes from the CPT/HCPCS code (see handleCptPick).
  const handleRevenuePick = (index, code) => {
    setFormData((prev) => {
      const newServices = prev.services.map((s, i) =>
        i === index ? { ...s, revenueCode: code } : s
      );
      return { ...prev, services: newServices };
    });
  };

  // A CPT/HCPCS code was resolved to its fee-schedule description + charge (picked from the
  // dropdown, or an exact code typed and confirmed on blur). On an explicit pick we fill both
  // description and charge; on a blur auto-resolve we only fill fields the biller left empty.
  const handleCptPick = (index, code, { description, charge }, { explicit }) => {
    setFormData((prev) => {
      const newServices = prev.services.map((s, i) => {
        if (i !== index) return s;
        const next = { ...s, hcpcs: code };
        if (description && (explicit || !s.description?.trim())) next.description = description;
        if (charge != null && charge !== '' && (explicit || !String(s.totalCharges).trim())) {
          next.totalCharges = Number(charge).toFixed(2);
        }
        return next;
      });
      return { ...prev, services: newServices };
    });
  };

  const addRevenueLine = () => {
    if (formData.services.length < 23) {
      setFormData((prev) => ({
        ...prev,
        services: [...prev.services, createEmptyRevenueLine()],
      }));
    }
  };

  const removeRevenueLine = (index) => {
    if (formData.services.length > 1) {
      setFormData((prev) => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index),
      }));
    }
  };

  const rawInsurances    = claim?.patient?.insurances ?? [];
  const patientProfileId = claim?.patient?.id ?? claim?.patient?.patient_id ?? claim?.patient_db_id;

  // When the claim's payer is "frozen" — an in-place Change Payer (payer_overridden) OR a
  // rebill/correction (rebilled_from / freq 7) — the billed payer lives on the claim
  // (claim_payers / claim.payers), NOT on the patient's current insurance profile. Show and
  // resubmit that frozen snapshot so the form reflects what will actually be billed instead
  // of the patient's live primary slot (which can differ — e.g. a rebill switched the payer).
  // For every other claim we keep the existing behaviour: derive from patient_insurance.
  // Mirrors CMS1500Form; the reverse relationship map yields the capitalized words that
  // REL_TO_1450 keys on so the existing save/display code works unchanged.
  const X12_TO_REL_1450 = { '18': 'Self', '01': 'Spouse', '19': 'Child', '20': 'Employee', '53': 'Life Partner', G8: 'Other' };
  const effectiveInsurances = useMemo(() => {
    if (!claim?.payer_frozen) return rawInsurances;
    const claimPayers = claim?.payers ?? [];
    return [0, 1, 2].map((i) => {
      const pi = rawInsurances[i] ?? null;
      const cp = claimPayers.find((p) => Number(p.payer_order) === i + 1);
      if (!cp || !cp.payer_name) return pi;
      // Overlay the claim's frozen payer onto the patient_insurance display shape so
      // the existing render/save code (which reads insurance_provider/policy_number/…)
      // works unchanged.
      return {
        ...(pi || {}),
        insurance_provider:     cp.payer_name,
        policy_number:          cp.insured_id ?? pi?.policy_number ?? null,
        group_number:           cp.group_number ?? pi?.group_number ?? null,
        group_name:             cp.group_name ?? pi?.group_name ?? null,
        subscriber_name:        cp.insured_name ?? pi?.subscriber_name ?? null,
        subscriber_relation:    X12_TO_REL_1450[cp.insured_relationship] ?? pi?.subscriber_relation ?? null,
        health_plan_id:         cp.health_plan_id ?? pi?.health_plan_id ?? null,
        authorization_code:     cp.authorization_code ?? pi?.authorization_code ?? null,
        employer_name:          cp.employer_name ?? pi?.employer_name ?? null,
        release_of_info:        cp.release_of_info ?? pi?.release_of_info ?? 'Y',
        assignment_of_benefits: cp.assignment_of_benefits ?? pi?.assignment_of_benefits ?? 'Y',
      };
    });
  }, [claim?.payer_frozen, claim?.payers, rawInsurances]);

  const updateDiagnosis = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      diagnoses: { ...prev.diagnoses, [field]: value },
    }));
  };

  const updateDiagArray = (field, index, value) => {
    setFormData((prev) => {
      const arr = [...prev.diagnoses[field]];
      arr[index] = value;
      return { ...prev, diagnoses: { ...prev.diagnoses, [field]: arr } };
    });
  };

  const updatePayerFinancial = (index, field, value) => {
    setFormData((prev) => {
      const arr = prev.payerFinancials.map((pf, i) => i === index ? { ...pf, [field]: value } : pf);
      return { ...prev, payerFinancials: arr };
    });
  };

  const updatePhysician = (role, field, value) => {
    setFormData((prev) => ({
      ...prev,
      physicians: {
        ...prev.physicians,
        [role]: { ...prev.physicians[role], [field]: value },
      },
    }));
  };

  const totalCharges = useMemo(() => {
    return formData.services
      .reduce((sum, s) => sum + (parseFloat(s.totalCharges) || 0), 0)
      .toFixed(2);
  }, [formData.services]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    try {
      const isCobChild = cobMode && cobOrder > 1;
      const cobPayerData = isCobChild ? (() => {
        const ins = rawInsurances[cobOrder - 1];
        // Estimated amount due from this payer = the balance forwarded into this tier.
        // Prefer the running balance the COB timeline computed (forwarded_balance) — it
        // stays correct when the prior payer's ERA is sparse (allowed 0 / no PR line),
        // where allowed − paid would otherwise read $0. Fall back to the prior remit's
        // own fields when the timeline didn't supply it.
        const balanceFwd = cobPriorRemit
          ? (cobPriorRemit.forwarded_balance != null
              ? parseFloat(cobPriorRemit.forwarded_balance)
              : parseFloat(cobPriorRemit.patient_responsibility || 0) > 0
                ? parseFloat(cobPriorRemit.patient_responsibility)
                : Math.max(0, parseFloat(cobPriorRemit.allowed_amount || 0) - parseFloat(cobPriorRemit.paid_amount || 0)))
          : null;
        return {
          name:                 cobPayer?.payer_name || cobPayer?.insurance_provider || ins?.insurance_provider || null,
          insuredName:          cobPayer?.insured_name || cobPayer?.subscriber_name || ins?.subscriber_name || null,
          insuredId:            cobPayer?.insured_id || cobPayer?.policy_number || ins?.policy_number || null,
          relationship:         REL_TO_1450[cobPayer?.subscriber_relation] || cobPayer?.insured_relationship || REL_TO_1450[ins?.subscriber_relation] || '18',
          groupName:            cobPayer?.group_name || ins?.group_name || null,
          groupNumber:          cobPayer?.group_number || ins?.group_number || null,
          releaseOfInfo:        'Y',
          assignmentOfBenefits: 'Y',
          priorPayments:        cobPriorRemit?.paid_amount != null ? parseFloat(cobPriorRemit.paid_amount).toFixed(2) : null,
          estimatedAmountDue:   balanceFwd !== null ? balanceFwd.toFixed(2) : null,
        };
      })() : null;

      const procedures = [];
      if (formData.diagnoses.principalProcedure) {
        procedures.push({
          code: formData.diagnoses.principalProcedure,
          date: formData.diagnoses.principalProcedureDate || null,
          isPrincipal: true,
        });
      }
      formData.diagnoses.otherProcedures.forEach((code, i) => {
        if (code) {
          procedures.push({
            code,
            date: formData.diagnoses.otherProcedureDates[i] || null,
            isPrincipal: false,
          });
        }
      });

      const conditionCodes = formData.conditionCodes.filter(Boolean);

      const occurrenceCodes = formData.occurrenceCodes
        .map((code, i) => ({ code, date: formData.occurrenceDates[i] }))
        .filter((o) => o.code);

      const occurrenceSpanCodes = formData.occurrenceSpanCodes
        .map((code, i) => ({
          code,
          from: formData.occurrenceSpanFrom[i],
          through: formData.occurrenceSpanThrough[i],
        }))
        .filter((o) => o.code);

      const valueCodes = formData.valueCodes
        .map((code, i) => ({ code, amount: formData.valueAmounts[i] }))
        .filter((v) => v.code);

      const payers = [0, 1, 2].map((i) => {
        const ins = effectiveInsurances[i];
        if (!ins) return null;
        const fin = formData.payerFinancials[i];
        return {
          name:                 ins.insurance_provider     || null,
          healthPlanId:         ins.health_plan_id         || null,
          releaseOfInfo:        ins.release_of_info        || 'Y',
          assignmentOfBenefits: ins.assignment_of_benefits || 'Y',
          insuredName:          ins.subscriber_name        || null,
          relationship:         REL_TO_1450[ins.subscriber_relation] || '18',
          insuredId:            ins.policy_number          || null,
          groupName:            ins.group_name             || null,
          groupNumber:          ins.group_number           || null,
          authorizationCode:    ins.authorization_code     || null,
          employerName:         ins.employer_name          || null,
          priorPayments:        fin?.priorPayments      || null,
          estimatedAmountDue:   fin?.estimatedAmountDue || null,
        };
      }).filter(Boolean);

      const payload = {
        // The server refuses (409) a body built by a form other than the one the claim
        // resolves to, instead of misreading it and dropping the lines.
        formType:    'CMS-1450',
        cobMode:     !!cobMode,
        cobOrder:    cobOrder || null,
        cobPayerData: cobPayerData || null,
        claim_details: {
          statementFrom:       formData.statementFrom || null,
          statementThrough:    formData.statementThrough || null,
          admissionDate:       formData.patient.admissionDate || null,
          admissionHour:       formData.patient.admissionHour || null,
          admissionType:       formData.patient.admissionType || null,
          admissionSource:     formData.patient.admissionSource || null,
          dischargeHour:       formData.patient.dischargeHour || null,
          dischargeStatus:     formData.patient.dischargeStatus || null,
          typeOfBill:          formData.provider.typeOfBill || null,
          medicalRecordNumber: formData.patient.medicalRecordNumber || null,
          icdVersion:          formData.diagnoses.icdVersion,
          ppsCode:             formData.diagnoses.ppsCode || null,
          attendingProvider:   formData.physicians.attending,
          operatingProvider:   formData.physicians.operating,
          otherProvider1:      formData.physicians.other1,
          otherProvider2:      formData.physicians.other2,
          remarks:             formData.remarks || null,
        },
        diagnoses: {
          principal:      formData.diagnoses.principal
            ? { code: formData.diagnoses.principal, poa: formData.diagnoses.principalPoa || null }
            : null,
          admitting:      formData.diagnoses.admitting
            ? { code: formData.diagnoses.admitting, poa: formData.diagnoses.admittingPoa || null }
            : null,
          other: formData.diagnoses.other.map((code, i) =>
            code ? { code, poa: formData.diagnoses.otherPoa[i] || null } : null
          ).filter(Boolean),
          reasonForVisit: formData.diagnoses.reasonForVisit.filter(Boolean),
          externalCause:  formData.diagnoses.externalCause.filter(Boolean),
        },
        services: formData.services,
        procedures,
        payers,
        conditionCodes,
        occurrenceCodes,
        occurrenceSpanCodes,
        valueCodes,
        financial: {
          totalCharge: parseFloat(totalCharges) || 0,
          amountPaid:  0,
          balanceDue:  parseFloat(totalCharges) || 0,
        },
      };

      const result = await generateClaim(claim.id, payload);

      if (isCobChild && result?.claimId) {
        navigate(`/claims/${result.claimId}`);
      } else {
        navigate(returnTo || '/worklist');
      }
    } catch (err) {
      setSaveError(
        err?.response?.data?.error || err?.message || 'Failed to save claim. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const icons = {
    provider: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    patient: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    ),
    admission: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    codes: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    revenue: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="18" rx="2" /><line x1="2" y1="9" x2="22" y2="9" /><line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    ),
    payer: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    diagnosis: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    physician: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
      </svg>
    ),
    remarks: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* 1. Provider / Facility Information */}
      <Section icon={icons.provider} title="Provider / Facility Information (FL 1-6)">
        <div className="gc-grid">
          <div className="gc-field gc-span-2">
            <label className="gc-label">Provider Name <span className="gc-label-hint">FL 1</span></label>
            <input className="gc-input prefilled" type="text" value={formData.provider.name} onChange={(e) => updateField('provider', 'name', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Phone</label>
            <input className="gc-input prefilled" type="tel" value={formData.provider.phone} onChange={(e) => updateField('provider', 'phone', e.target.value)} />
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Address</label>
            <input className="gc-input prefilled" type="text" value={formData.provider.address} onChange={(e) => updateField('provider', 'address', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">City</label>
            <input className="gc-input prefilled" type="text" value={formData.provider.city} onChange={(e) => updateField('provider', 'city', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">State</label>
            <input className="gc-input prefilled" type="text" value={formData.provider.state} onChange={(e) => updateField('provider', 'state', e.target.value)} maxLength={2} />
          </div>
          <div className="gc-field">
            <label className="gc-label">ZIP</label>
            <input className="gc-input prefilled" type="text" value={formData.provider.zip} onChange={(e) => updateField('provider', 'zip', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Federal Tax Number <span className="gc-label-hint">FL 5</span></label>
            <input className="gc-input prefilled" type="text" value={formData.provider.federalTaxNumber} onChange={(e) => updateField('provider', 'federalTaxNumber', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">NPI <span className="gc-label-hint">FL 56</span></label>
            <input className="gc-input prefilled" type="text" value={formData.provider.npi} onChange={(e) => updateField('provider', 'npi', e.target.value)} maxLength={10} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Taxonomy Code <span className="gc-label-hint">FL 81CCa</span></label>
            <input className="gc-input" type="text" value={formData.provider.taxonomyCode} onChange={(e) => updateField('provider', 'taxonomyCode', e.target.value)} placeholder="e.g. 282N00000X" maxLength={10} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Type of Bill <span className="gc-label-hint">FL 4</span></label>
            <input className="gc-input" type="text" value={formData.provider.typeOfBill} onChange={(e) => updateField('provider', 'typeOfBill', e.target.value)} placeholder="e.g. 0111" maxLength={4} />
          </div>
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">Pay-To (if different from above) — FL 2</div>
        <div className="gc-grid">
          <div className="gc-field gc-span-2">
            <label className="gc-label">Pay-To Name</label>
            <input className="gc-input" type="text" value={formData.provider.payToName} onChange={(e) => updateField('provider', 'payToName', e.target.value)} placeholder="Leave blank if same as provider" />
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Pay-To Address</label>
            <input className="gc-input" type="text" value={formData.provider.payToAddress} onChange={(e) => updateField('provider', 'payToAddress', e.target.value)} />
          </div>
        </div>
      </Section>

      {/* 2. Patient Information */}
      <Section icon={icons.patient} title="Patient Information (FL 8-11)">
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Patient Control # <span className="gc-label-hint">FL 3a</span></label>
            <input className="gc-input prefilled" type="text" value={formData.patient.controlNumber} onChange={(e) => updateField('patient', 'controlNumber', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Medical Record # <span className="gc-label-hint">FL 3b</span></label>
            <input className="gc-input" type="text" value={formData.patient.medicalRecordNumber} onChange={(e) => updateField('patient', 'medicalRecordNumber', e.target.value)} />
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Patient Name <span className="gc-label-hint">FL 8b</span></label>
            <input className="gc-input prefilled" type="text" value={formData.patient.name} onChange={(e) => updateField('patient', 'name', e.target.value)} />
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Address <span className="gc-label-hint">FL 9</span></label>
            <input className="gc-input" type="text" value={formData.patient.address} onChange={(e) => updateField('patient', 'address', e.target.value)} placeholder="Street address" />
          </div>
          <div className="gc-field">
            <label className="gc-label">City</label>
            <input className="gc-input" type="text" value={formData.patient.city} onChange={(e) => updateField('patient', 'city', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">State</label>
            <input className="gc-input" type="text" value={formData.patient.state} onChange={(e) => updateField('patient', 'state', e.target.value)} maxLength={2} />
          </div>
          <div className="gc-field">
            <label className="gc-label">ZIP</label>
            <input className="gc-input" type="text" value={formData.patient.zip} onChange={(e) => updateField('patient', 'zip', e.target.value)} maxLength={10} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Date of Birth <span className="gc-label-hint">FL 10</span></label>
            <input className="gc-input" type="date" value={formData.patient.birthDate} onChange={(e) => updateField('patient', 'birthDate', e.target.value)} onKeyDown={onDateKeyDown(formData.patient.birthDate)} onFocus={onDateFocus(formData.patient.birthDate, (v) => updateField('patient', 'birthDate', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Sex <span className="gc-label-hint">FL 11</span></label>
            <select className="gc-select" value={formData.patient.sex} onChange={(e) => updateField('patient', 'sex', e.target.value)}>
              <option value="">Select...</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="U">Unknown</option>
            </select>
          </div>
        </div>
      </Section>

      {/* 3. Admission Details */}
      <Section icon={icons.admission} title="Admission Details (FL 12-17)">
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Statement From <span className="gc-label-hint">FL 6</span></label>
            <input className="gc-input" type="date" value={formData.statementFrom} onChange={(e) => updateTopLevel('statementFrom', e.target.value)} onKeyDown={onDateKeyDown(formData.statementFrom)} onFocus={onDateFocus(formData.statementFrom, (v) => updateTopLevel('statementFrom', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Statement Through <span className="gc-label-hint">FL 6</span></label>
            <input className="gc-input" type="date" value={formData.statementThrough} onChange={(e) => updateTopLevel('statementThrough', e.target.value)} onKeyDown={onDateKeyDown(formData.statementThrough)} onFocus={onDateFocus(formData.statementThrough, (v) => updateTopLevel('statementThrough', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Admission Date <span className="gc-label-hint">FL 12</span></label>
            <input className="gc-input" type="date" value={formData.patient.admissionDate} onChange={(e) => updateField('patient', 'admissionDate', e.target.value)} onKeyDown={onDateKeyDown(formData.patient.admissionDate)} onFocus={onDateFocus(formData.patient.admissionDate, (v) => updateField('patient', 'admissionDate', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Admission Hour <span className="gc-label-hint">FL 13</span></label>
            <input className="gc-input" type="time" value={formData.patient.admissionHour} onChange={(e) => updateField('patient', 'admissionHour', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Admission Type <span className="gc-label-hint">FL 14</span></label>
            <select className="gc-select" value={formData.patient.admissionType} onChange={(e) => updateField('patient', 'admissionType', e.target.value)}>
              {ADMISSION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="gc-field">
            <label className="gc-label">Admission Source <span className="gc-label-hint">FL 15</span></label>
            <select className="gc-select" value={formData.patient.admissionSource} onChange={(e) => updateField('patient', 'admissionSource', e.target.value)}>
              {ADMISSION_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="gc-field">
            <label className="gc-label">Discharge Hour <span className="gc-label-hint">FL 16</span></label>
            <input className="gc-input" type="time" value={formData.patient.dischargeHour} onChange={(e) => updateField('patient', 'dischargeHour', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Discharge Status <span className="gc-label-hint">FL 17</span></label>
            <select className="gc-select" value={formData.patient.dischargeStatus} onChange={(e) => updateField('patient', 'dischargeStatus', e.target.value)}>
              {DISCHARGE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
      </Section>

      {/* 4. Condition / Occurrence / Value Codes */}
      <Section icon={icons.codes} title="Condition, Occurrence & Value Codes (FL 18-41)" defaultOpen={false}>
        {/* Condition Codes */}
        <div className="gc-inner-label">Condition Codes (FL 18-28) — up to 11</div>
        <div className="gc-code-grid" style={{ marginBottom: 20 }}>
          {formData.conditionCodes.map((code, i) => (
            <div key={i} className="gc-field">
              <label className="gc-label">{i + 18}</label>
              <input className="gc-code-input" type="text" value={code} onChange={(e) => updateArrayItem('conditionCodes', i, e.target.value)} placeholder="Code" maxLength={2} />
            </div>
          ))}
        </div>

        <hr className="gc-inner-divider" />

        {/* Occurrence Codes */}
        <div className="gc-inner-label">Occurrence Codes & Dates (FL 31-34) — up to 8</div>
        <div className="gc-grid gc-grid-4" style={{ marginBottom: 20 }}>
          {formData.occurrenceCodes.map((code, i) => (
            <div key={i} className="gc-code-pair">
              <input className="gc-code-input" type="text" value={code} onChange={(e) => updateArrayItem('occurrenceCodes', i, e.target.value)} placeholder="Code" maxLength={2} />
              <input className="gc-input" type="date" value={formData.occurrenceDates[i]} onChange={(e) => updateArrayItem('occurrenceDates', i, e.target.value)} onKeyDown={onDateKeyDown(formData.occurrenceDates[i])} onFocus={onDateFocus(formData.occurrenceDates[i], (v) => updateArrayItem('occurrenceDates', i, v))} style={{ fontSize: '0.8rem' }} />
            </div>
          ))}
        </div>

        <hr className="gc-inner-divider" />

        {/* Occurrence Span Codes */}
        <div className="gc-inner-label">Occurrence Span Codes (FL 35-36) — up to 4</div>
        <div className="gc-grid gc-grid-2" style={{ marginBottom: 20 }}>
          {formData.occurrenceSpanCodes.map((code, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'end' }}>
              <div className="gc-field" style={{ width: 70 }}>
                <label className="gc-label">Code</label>
                <input className="gc-code-input" type="text" value={code} onChange={(e) => updateArrayItem('occurrenceSpanCodes', i, e.target.value)} maxLength={2} />
              </div>
              <div className="gc-field" style={{ flex: 1 }}>
                <label className="gc-label">From</label>
                <input className="gc-input" type="date" value={formData.occurrenceSpanFrom[i]} onChange={(e) => updateArrayItem('occurrenceSpanFrom', i, e.target.value)} onKeyDown={onDateKeyDown(formData.occurrenceSpanFrom[i])} onFocus={onDateFocus(formData.occurrenceSpanFrom[i], (v) => updateArrayItem('occurrenceSpanFrom', i, v))} />
              </div>
              <div className="gc-field" style={{ flex: 1 }}>
                <label className="gc-label">Through</label>
                <input className="gc-input" type="date" value={formData.occurrenceSpanThrough[i]} onChange={(e) => updateArrayItem('occurrenceSpanThrough', i, e.target.value)} onKeyDown={onDateKeyDown(formData.occurrenceSpanThrough[i])} onFocus={onDateFocus(formData.occurrenceSpanThrough[i], (v) => updateArrayItem('occurrenceSpanThrough', i, v))} />
              </div>
            </div>
          ))}
        </div>

        <hr className="gc-inner-divider" />

        {/* Value Codes */}
        <div className="gc-inner-label">Value Codes & Amounts (FL 39-41) — up to 12</div>
        <div className="gc-grid gc-grid-4">
          {formData.valueCodes.map((code, i) => (
            <div key={i} className="gc-code-pair">
              <input className="gc-code-input" type="text" value={code} onChange={(e) => updateArrayItem('valueCodes', i, e.target.value)} placeholder="Code" maxLength={2} />
              <input className="gc-input" type="number" step="0.01" value={formData.valueAmounts[i]} onChange={(e) => updateArrayItem('valueAmounts', i, e.target.value)} placeholder="Amount" style={{ fontSize: '0.8rem' }} />
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Revenue / Service Lines */}
      <Section icon={icons.revenue} title="Revenue / Service Lines (FL 42-49)">
        <div className={`gc-rev-scroll ${openRevenueMenus > 0 ? 'gc-rev-scroll-menu-open' : ''}`}>
          <div className="gc-revenue-header">
            <span>Rev Code</span>
            <span>Description</span>
            <span>HCPCS + Mods</span>
            <span>Service Date</span>
            <span>Units</span>
            <span>Total Charges</span>
            <span>Non-Covered</span>
            <span></span>
          </div>
          {formData.services.map((svc, i) => (
            <div key={i} className="gc-revenue-line">
              <RevenueCodeCombobox
                code={svc.revenueCode}
                facilityId={facility?.id}
                onCodeChange={(v) => updateService(i, 'revenueCode', v)}
                onPick={(c, d, opts) => handleRevenuePick(i, c, d, opts)}
                onOpenChange={handleRevenueMenuOpenChange}
              />
              <input className="gc-rev-input" type="text" value={svc.description} onChange={(e) => updateService(i, 'description', e.target.value)} placeholder="Description" />
              <div className="gc-rev-hcpcs-cell">
                <CptCodeCombobox
                  code={svc.hcpcs}
                  facilityId={facility?.id}
                  inputClassName="gc-rev-input"
                  placeholder="CPT/HCPCS"
                  onCodeChange={(v) => updateService(i, 'hcpcs', v)}
                  onPick={(c, vals, opts) => handleCptPick(i, c, vals, opts)}
                  onOpenChange={handleRevenueMenuOpenChange}
                />
                <input className="gc-rev-input gc-rev-modifiers" type="text" value={svc.modifiers} onChange={(e) => updateService(i, 'modifiers', e.target.value)} placeholder="Mods" title="Up to 4 two-character modifiers, space-separated (e.g. 25 59)" />
              </div>
              <input className="gc-rev-input" type="date" value={svc.serviceDate} onChange={(e) => updateService(i, 'serviceDate', e.target.value)} onKeyDown={onDateKeyDown(svc.serviceDate)} onFocus={onDateFocus(svc.serviceDate, (v) => updateService(i, 'serviceDate', v))} />
              <input className="gc-rev-input" type="number" min="0" value={svc.units} onChange={(e) => updateService(i, 'units', e.target.value)} placeholder="0" />
              <input className="gc-rev-input" type="number" step="0.01" min="0" value={svc.totalCharges} onChange={(e) => updateService(i, 'totalCharges', e.target.value)} placeholder="0.00" />
              <input className="gc-rev-input" type="number" step="0.01" min="0" value={svc.nonCoveredCharges} onChange={(e) => updateService(i, 'nonCoveredCharges', e.target.value)} placeholder="0.00" />
              <button type="button" className="gc-btn-remove-service" onClick={() => removeRevenueLine(i)} disabled={formData.services.length <= 1}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <button type="button" className="gc-btn-add-service" onClick={addRevenueLine} disabled={formData.services.length >= 23}>
            + Add Revenue Line
          </button>
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#171363' }}>
            Total: ${totalCharges}
          </div>
        </div>
      </Section>

      {/* 6. Payer Information — read-only; edit via Patient Profile */}
      <Section icon={icons.payer} title="Payer Information (FL 50-65)">
        <div className="gc-payer-tabs">
          {['Primary (A)', 'Secondary (B)', 'Tertiary (C)'].map((label, i) => (
            <button type="button" key={i} className={`gc-payer-tab ${activePayer === i ? 'active' : ''}`} onClick={() => setActivePayer(i)}>
              {label}
            </button>
          ))}
        </div>
        {(() => {
          const ins = effectiveInsurances[activePayer];
          const slotLabel = ['Primary', 'Secondary', 'Tertiary'][activePayer];
          if (!ins) {
            return (
              <div className="gc-ins-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <p>No {slotLabel.toLowerCase()} insurance on file for this patient.</p>
                {patientProfileId && (
                  <Link to={`/patients/${patientProfileId}`} className="gc-ins-add-btn">
                    + Add {slotLabel} Insurance
                  </Link>
                )}
              </div>
            );
          }
          return (
            <div className="gc-ins-readonly">
              <div className="gc-ins-readonly-header">
                <span className="gc-ins-payer-name">{ins.insurance_provider || '—'}</span>
                {patientProfileId && (
                  <Link to={`/patients/${patientProfileId}`} className="gc-ins-edit-link">
                    Edit in Patient Profile →
                  </Link>
                )}
              </div>
              <div className="gc-ins-detail-grid">
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Member ID <span className="gc-label-hint">FL 60</span></span>
                  <span className="gc-ins-detail-value">{ins.policy_number || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Health Plan ID <span className="gc-label-hint">FL 51</span></span>
                  <span className="gc-ins-detail-value">{ins.health_plan_id || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Group Number <span className="gc-label-hint">FL 62</span></span>
                  <span className="gc-ins-detail-value">{ins.group_number || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Group Name <span className="gc-label-hint">FL 61</span></span>
                  <span className="gc-ins-detail-value">{ins.group_name || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Subscriber <span className="gc-label-hint">FL 58</span></span>
                  <span className="gc-ins-detail-value">{ins.subscriber_name || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Relationship <span className="gc-label-hint">FL 59</span></span>
                  <span className="gc-ins-detail-value">{ins.subscriber_relation || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Authorization Code <span className="gc-label-hint">FL 63</span></span>
                  <span className="gc-ins-detail-value">{ins.authorization_code || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Employer <span className="gc-label-hint">FL 65</span></span>
                  <span className="gc-ins-detail-value">{ins.employer_name || '—'}</span>
                </div>
                {(ins.payer_address || ins.payer_city) && (
                  <div className="gc-ins-detail-item gc-ins-span-2">
                    <span className="gc-ins-detail-label">Payer Address</span>
                    <span className="gc-ins-detail-value">
                      {[ins.payer_address, ins.payer_city, ins.payer_state, ins.payer_zip].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {/* FL 54 / 55 – Claim-level financial fields, one set per payer tab */}
        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">
          Claim Financials — {['Primary (A)', 'Secondary (B)', 'Tertiary (C)'][activePayer]}
        </div>
        <div className="gc-grid gc-grid-2">
          <div className="gc-field">
            <label className="gc-label">Prior Payments <span className="gc-label-hint">FL 54</span></label>
            <input
              className="gc-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.payerFinancials[activePayer].priorPayments}
              onChange={(e) => updatePayerFinancial(activePayer, 'priorPayments', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="gc-field">
            <label className="gc-label">Est. Amount Due <span className="gc-label-hint">FL 55</span></label>
            <input
              className="gc-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.payerFinancials[activePayer].estimatedAmountDue}
              onChange={(e) => updatePayerFinancial(activePayer, 'estimatedAmountDue', e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>
      </Section>

      {/* 7. Diagnosis & Procedure Codes */}
      <Section icon={icons.diagnosis} title="Diagnosis & Procedure Codes (FL 66-74)">
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">ICD Version <span className="gc-label-hint">FL 66</span></label>
            <select className="gc-select" value={formData.diagnoses.icdVersion} onChange={(e) => updateDiagnosis('icdVersion', e.target.value)}>
              <option value="0">0 – ICD-10-CM</option>
              <option value="9">9 – ICD-9-CM</option>
            </select>
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Principal Diagnosis <span className="gc-label-hint">FL 67</span></label>
            <input className="gc-input" type="text" value={formData.diagnoses.principal} onChange={(e) => updateDiagnosis('principal', e.target.value)} placeholder="ICD-10 code" />
          </div>
          <div className="gc-field">
            <label className="gc-label">Principal POA</label>
            <select className="gc-select" value={formData.diagnoses.principalPoa} onChange={(e) => updateDiagnosis('principalPoa', e.target.value)}>
              {POA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="gc-field">
            <label className="gc-label">Admitting Diagnosis <span className="gc-label-hint">FL 69</span></label>
            <input className="gc-input" type="text" value={formData.diagnoses.admitting} onChange={(e) => updateDiagnosis('admitting', e.target.value)} placeholder="ICD-10 code" />
          </div>
          <div className="gc-field">
            <label className="gc-label">Admitting POA</label>
            <select className="gc-select" value={formData.diagnoses.admittingPoa} onChange={(e) => updateDiagnosis('admittingPoa', e.target.value)}>
              {POA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">Other Diagnosis Codes (FL 67A-Q) — up to 17</div>
        <div className="gc-diag-grid">
          {formData.diagnoses.other.map((code, i) => (
            <div key={i} className="gc-diag-field">
              <span className="gc-diag-letter">{String.fromCharCode(65 + i)}</span>
              <input className="gc-diag-input" type="text" value={code} onChange={(e) => updateDiagArray('other', i, e.target.value)} placeholder="ICD-10" />
              <select className="gc-poa-select" value={formData.diagnoses.otherPoa[i]} onChange={(e) => updateDiagArray('otherPoa', i, e.target.value)} title="Present on Admission">
                {POA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value || '—'}</option>)}
              </select>
            </div>
          ))}
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">Reason for Visit (FL 70) — up to 3 (Outpatient)</div>
        <div className="gc-grid gc-grid-3">
          {formData.diagnoses.reasonForVisit.map((code, i) => (
            <div key={i} className="gc-field">
              <label className="gc-label">Reason {i + 1}</label>
              <input className="gc-input" type="text" value={code} onChange={(e) => updateDiagArray('reasonForVisit', i, e.target.value)} placeholder="ICD-10 code" />
            </div>
          ))}
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">PPS / DRG Code <span className="gc-label-hint">FL 71</span></label>
            <input className="gc-input" type="text" value={formData.diagnoses.ppsCode} onChange={(e) => updateDiagnosis('ppsCode', e.target.value)} />
          </div>
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">External Cause of Injury (FL 72) — up to 3</div>
        <div className="gc-grid gc-grid-3">
          {formData.diagnoses.externalCause.map((code, i) => (
            <div key={i} className="gc-field">
              <label className="gc-label">ECI {i + 1}</label>
              <input className="gc-input" type="text" value={code} onChange={(e) => updateDiagArray('externalCause', i, e.target.value)} placeholder="ICD-10 code" />
            </div>
          ))}
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">Procedure Codes (FL 74) — ICD-10-PCS</div>
        <div className="gc-grid">
          <div className="gc-field gc-span-2">
            <label className="gc-label">Principal Procedure</label>
            <input className="gc-input" type="text" value={formData.diagnoses.principalProcedure} onChange={(e) => updateDiagnosis('principalProcedure', e.target.value)} placeholder="ICD-10-PCS code" />
          </div>
          <div className="gc-field">
            <label className="gc-label">Procedure Date</label>
            <input className="gc-input" type="date" value={formData.diagnoses.principalProcedureDate} onChange={(e) => updateDiagnosis('principalProcedureDate', e.target.value)} onKeyDown={onDateKeyDown(formData.diagnoses.principalProcedureDate)} onFocus={onDateFocus(formData.diagnoses.principalProcedureDate, (v) => updateDiagnosis('principalProcedureDate', v))} />
          </div>
        </div>
        <div className="gc-grid gc-grid-3" style={{ marginTop: 12 }}>
          {formData.diagnoses.otherProcedures.map((code, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'end' }}>
              <div className="gc-field" style={{ flex: 1 }}>
                <label className="gc-label">Other {i + 1}</label>
                <input className="gc-input" type="text" value={code} onChange={(e) => updateDiagArray('otherProcedures', i, e.target.value)} placeholder="Code" />
              </div>
              <div className="gc-field" style={{ flex: 1 }}>
                <label className="gc-label">Date</label>
                <input className="gc-input" type="date" value={formData.diagnoses.otherProcedureDates[i]} onChange={(e) => updateDiagArray('otherProcedureDates', i, e.target.value)} onKeyDown={onDateKeyDown(formData.diagnoses.otherProcedureDates[i])} onFocus={onDateFocus(formData.diagnoses.otherProcedureDates[i], (v) => updateDiagArray('otherProcedureDates', i, v))} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8. Physician Information */}
      <Section icon={icons.physician} title="Physician Information (FL 76-79)">
        {/* Attending — FL 76 */}
        <div className="gc-inner-label">Attending Provider — FL 76</div>
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Last Name</label>
            <input className="gc-input prefilled" type="text" value={formData.physicians.attending.lastName} onChange={(e) => updatePhysician('attending', 'lastName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">First Name</label>
            <input className="gc-input prefilled" type="text" value={formData.physicians.attending.firstName} onChange={(e) => updatePhysician('attending', 'firstName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Middle</label>
            <input className="gc-input prefilled" type="text" value={formData.physicians.attending.middleName} onChange={(e) => updatePhysician('attending', 'middleName', e.target.value)} placeholder="MI" />
          </div>
          <div className="gc-field">
            <label className="gc-label">NPI</label>
            <input className="gc-input" type="text" value={formData.physicians.attending.npi} onChange={(e) => updatePhysician('attending', 'npi', e.target.value)} placeholder="10-digit NPI" maxLength={10} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Taxonomy</label>
            <input className="gc-input" type="text" value={formData.physicians.attending.taxonomyCode} onChange={(e) => updatePhysician('attending', 'taxonomyCode', e.target.value)} placeholder="e.g. 207R00000X" maxLength={10} />
          </div>
        </div>

        <hr className="gc-inner-divider" />
        {/* Operating — FL 77 */}
        <div className="gc-inner-label">Operating Provider — FL 77</div>
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Last Name</label>
            <input className="gc-input" type="text" value={formData.physicians.operating.lastName} onChange={(e) => updatePhysician('operating', 'lastName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">First Name</label>
            <input className="gc-input" type="text" value={formData.physicians.operating.firstName} onChange={(e) => updatePhysician('operating', 'firstName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Middle</label>
            <input className="gc-input" type="text" value={formData.physicians.operating.middleName} onChange={(e) => updatePhysician('operating', 'middleName', e.target.value)} placeholder="MI" />
          </div>
          <div className="gc-field">
            <label className="gc-label">NPI</label>
            <input className="gc-input" type="text" value={formData.physicians.operating.npi} onChange={(e) => updatePhysician('operating', 'npi', e.target.value)} placeholder="10-digit NPI" maxLength={10} />
          </div>
        </div>

        <hr className="gc-inner-divider" />
        {/* Other Provider 1 — FL 78 */}
        <div className="gc-inner-label">Other Provider 1 — FL 78</div>
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Qualifier</label>
            <select className="gc-select" value={formData.physicians.other1.qualifier} onChange={(e) => updatePhysician('other1', 'qualifier', e.target.value)}>
              {OTHER_PROVIDER_QUALIFIERS.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
            </select>
          </div>
          <div className="gc-field">
            <label className="gc-label">Last Name</label>
            <input className="gc-input" type="text" value={formData.physicians.other1.lastName} onChange={(e) => updatePhysician('other1', 'lastName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">First Name</label>
            <input className="gc-input" type="text" value={formData.physicians.other1.firstName} onChange={(e) => updatePhysician('other1', 'firstName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Middle</label>
            <input className="gc-input" type="text" value={formData.physicians.other1.middleName} onChange={(e) => updatePhysician('other1', 'middleName', e.target.value)} placeholder="MI" />
          </div>
          <div className="gc-field">
            <label className="gc-label">NPI</label>
            <input className="gc-input" type="text" value={formData.physicians.other1.npi} onChange={(e) => updatePhysician('other1', 'npi', e.target.value)} maxLength={10} />
          </div>
        </div>

        <hr className="gc-inner-divider" />
        {/* Other Provider 2 — FL 79 */}
        <div className="gc-inner-label">Other Provider 2 — FL 79</div>
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Qualifier</label>
            <select className="gc-select" value={formData.physicians.other2.qualifier} onChange={(e) => updatePhysician('other2', 'qualifier', e.target.value)}>
              {OTHER_PROVIDER_QUALIFIERS.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
            </select>
          </div>
          <div className="gc-field">
            <label className="gc-label">Last Name</label>
            <input className="gc-input" type="text" value={formData.physicians.other2.lastName} onChange={(e) => updatePhysician('other2', 'lastName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">First Name</label>
            <input className="gc-input" type="text" value={formData.physicians.other2.firstName} onChange={(e) => updatePhysician('other2', 'firstName', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Middle</label>
            <input className="gc-input" type="text" value={formData.physicians.other2.middleName} onChange={(e) => updatePhysician('other2', 'middleName', e.target.value)} placeholder="MI" />
          </div>
          <div className="gc-field">
            <label className="gc-label">NPI</label>
            <input className="gc-input" type="text" value={formData.physicians.other2.npi} onChange={(e) => updatePhysician('other2', 'npi', e.target.value)} maxLength={10} />
          </div>
        </div>
      </Section>

      {/* 9. Remarks */}
      <Section icon={icons.remarks} title="Remarks (FL 80)" defaultOpen={false}>
        <div className="gc-field">
          <label className="gc-label">Remarks / Additional Information</label>
          <textarea className="gc-textarea" value={formData.remarks} onChange={(e) => updateTopLevel('remarks', e.target.value)} placeholder="Additional information, narrative descriptions, NDC codes..." rows={4} />
        </div>
      </Section>

      {/* Actions */}
      {saveError && (
        <div className="gc-save-error">{saveError}</div>
      )}
      <div className="gc-actions">
        <button type="button" className="gc-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" className="gc-btn-submit" disabled={saving}>
          {saving ? (
            <svg className="gc-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isEdit ? (
                <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>
              ) : (
                <><path d="M22 2L11 13" /><path d="M22 2L15 22l-4-9-9-4z" /></>
              )}
            </svg>
          )}
          {saving ? (isEdit ? 'Updating…' : 'Submitting…') : (isEdit ? 'Update Claim' : 'Submit Claim')}
        </button>
      </div>
    </form>
  );
}

export default CMS1450Form;
